import React, { useEffect } from 'react';
import { FiCheck } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AnnouncementBar from './AnnouncementBar';
import Header from './Header';
import Footer from './Footer';
import { serviceAreas, serviceLinks } from '../data/serviceAreas';

const MobileNotaryCityPage = ({ city }) => {
  useEffect(() => {
    document.title = `Mobile Notary in ${city.name}, MD | Pronto Notary`;
    const description = `Pronto Notary offers mobile and online notary services in ${city.name}, Maryland, with bilingual evening and weekend appointments.`;
    let tag = document.querySelector('meta[name="description"]');

    if (!tag) {
      tag = document.createElement('meta');
      tag.name = 'description';
      document.head.appendChild(tag);
    }

    tag.content = description;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ProfessionalService',
      name: 'Pronto Notary',
      description,
      telephone: '+1-240-571-3117',
      areaServed: {
        '@type': 'City',
        name: `${city.name}, Maryland`
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: city.name,
        addressRegion: 'MD',
        addressCountry: 'US'
      }
    });
    document.head.appendChild(script);

    return () => script.remove();
  }, [city, city.name]);

  return (
    <PageShell>
      <section className="bg-cream-dark px-6 pb-20 pt-16 md:pb-28 md:pt-24">
        <div className="mx-auto max-w-7xl">
          <Eyebrow>MOBILE NOTARY IN {city.name.toUpperCase()}, MD</Eyebrow>
          <h1 className="max-w-4xl font-fraunces text-5xl leading-tight text-navy md:text-7xl">
            Mobile Notary in {city.name}, MD
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-navy/70">
            {city.subhead}
          </p>
          {city.detail && <p className="mt-3 text-base text-navy/70">{city.detail}</p>}

          {city.portuguese && <PortugueseFeature content={city.portuguese} />}

          <CredentialStrip />
        </div>
      </section>

      <section className="bg-cream px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-2xl">
            <Eyebrow>SERVICES</Eyebrow>
            <h2 className="font-fraunces text-4xl text-navy md:text-5xl">
              Services Available in {city.name}
            </h2>
          </div>
          <div className="mt-10 grid gap-x-12 gap-y-4 md:grid-cols-2">
            {serviceLinks.map((service) => (
              <a
                key={service.href}
                href={service.href}
                className="flex items-center justify-between border-b border-line py-4 font-semibold text-navy transition-colors hover:text-brass"
              >
                {service.label}
                <span aria-hidden="true">→</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-dark px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <Eyebrow>LOCAL FAQ</Eyebrow>
          <h2 className="font-fraunces text-4xl text-navy md:text-5xl">Questions about {city.name}</h2>
          <div className="mt-10 space-y-4">
            <details className="rounded-2xl border border-line bg-cream p-6">
              <summary className="cursor-pointer font-semibold text-navy">
                Do you serve {city.name} regularly?
              </summary>
              <p className="mt-4 leading-relaxed text-navy/70">
                Yes, {city.name} is part of our regular coverage area in {city.county}.
              </p>
            </details>
            <details className="rounded-2xl border border-line bg-cream p-6">
              <summary className="cursor-pointer font-semibold text-navy">
                Can you meet me in the evening or on a weekend in {city.name}?
              </summary>
              <p className="mt-4 leading-relaxed text-navy/70">
                Yes. We offer appointments in the evenings and on weekends.
              </p>
            </details>
          </div>
        </div>
      </section>

      <section className="bg-navy px-6 py-20 text-center text-cream">
        <div className="mx-auto max-w-3xl">
          <Eyebrow>READY WHEN YOU ARE</Eyebrow>
          <h2 className="font-fraunces text-4xl md:text-5xl">Let&apos;s get it handled.</h2>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <a href="/contact" className="rounded-full bg-cream px-7 py-4 font-bold text-navy transition-colors hover:bg-brass hover:text-cream">
              Book Your Appointment
            </a>
            <a href="tel:2405713117" className="rounded-full border border-cream/50 px-7 py-4 font-bold text-cream transition-colors hover:border-brass hover:text-brass">
              Call or Text (240) 571-3117
            </a>
          </div>
          <a href="/service-areas" className="mt-8 inline-block text-sm font-semibold text-cream/70 transition-colors hover:text-brass-light">
            See all service areas <span aria-hidden="true">→</span>
          </a>
        </div>
      </section>
    </PageShell>
  );
};

const PortugueseFeature = ({ content }) => (
  <div className="mt-12 max-w-3xl border-l-2 border-brass pl-6">
    <Eyebrow>{content.eyebrow}</Eyebrow>
    <h2 className="font-fraunces text-3xl text-navy md:text-4xl">{content.heading}</h2>
    <p className="mt-4 leading-relaxed text-navy/70">{content.body}</p>
  </div>
);

const CredentialStrip = () => {
  const badges = ['MD Commissioned', 'RON Licensed', 'Bilingual EN/PT', 'E&O Insured'];

  return (
    <div className="mt-12 flex flex-wrap gap-3">
      {badges.map((badge) => (
        <span key={badge} className="inline-flex items-center gap-2 rounded-full border border-brass/40 bg-cream px-4 py-2 text-sm font-semibold text-navy">
          <SafeIcon icon={FiCheck} size={15} className="text-brass" />
          {badge}
        </span>
      ))}
    </div>
  );
};

const PageShell = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-cream selection:bg-brass selection:text-cream">
    <AnnouncementBar />
    <Header />
    <main className="flex-grow">{children}</main>
    <Footer />
  </div>
);

const Eyebrow = ({ children }) => (
  <span className="mb-5 block text-xs font-bold uppercase tracking-[0.2em] text-brass">
    {children}
  </span>
);

export default MobileNotaryCityPage;